import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { I18nProvider, useI18n } from "./contexts/I18nContext";
import { FeedFilterProvider } from "./contexts/FeedFilterContext";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Social from "./pages/Social";
import ResetPassword from "./pages/ResetPassword";
import ResetPasswordSent from './pages/ResetPasswordSent';
import NotFound from "./pages/NotFound";
import AuthCallback from './pages/AuthCallback';
import AuthError from './pages/AuthError';
import { useEffect } from 'react';
import { getBasename } from '@/utils/url';
import InviteFriend from './pages/InviteFriend';
import Contact from './pages/Contact';
import MyJourney from './pages/MyJourney';
import UserProfile from './pages/UserProfile';
import Search from './pages/Search';
import PostDetail from './pages/PostDetail';
import Calendar from './pages/Calendar';
import { UserPosts } from './pages/UserPosts';
import { UserConnections } from './pages/UserConnections';
import UserAchievements from './pages/UserAchievements';
import { UserRecognitions } from './pages/UserRecognitions';
import { InstructorSlots } from './pages/InstructorSlots';
import Home from './pages/Home';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicies from './pages/PrivacyPolicies';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const LoadingScreen = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#160C0C] text-white">
      <div className="text-lg font-medium">Loading...</div>
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

const PublicAccessRoute = ({ children }: { children: React.ReactNode }) => {
  const { loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return <>{children}</>;
};

const ResetPasswordRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();
  const [searchParams] = useSearchParams();

  // Forçar atualização do componente ao mudar o hash
  useEffect(() => {
    const onHashChange = () => {
      // Força re-render
      window.location.reload();
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  // Nunca bloqueie o acesso ao reset de senha
  return <>{children}</>;
};

const SocialRoute = () => {
  const { loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return <Social />;
};

// Wrapper components for profile detail pages
const UserPostsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const isOwnProfile = user?.id === userId;
  
  return <UserPosts userId={userId || ''} isOwnProfile={isOwnProfile} />;
};

const UserConnectionsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  const { user } = useAuth();
  const isOwnProfile = user?.id === userId;
  
  return <UserConnections userId={userId || ''} isOwnProfile={isOwnProfile} />;
};

const UserAchievementsRoute = () => {
  const { userId } = useParams<{ userId: string }>();
  
  return <UserAchievements />;
};

const UserRecognitionsRoute = () => {
  return <UserRecognitions />;
};

const AppRoutes = () => {
  return (
          <Routes>
            <Route path="/" element={
              <PublicRoute>
                <Index />
              </PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/home" element={
              <ProtectedRoute>
                <Home />
              </ProtectedRoute>
            } />
            <Route path="/social" element={
              <SocialRoute />
            } />
            <Route path="/profile/:username" element={<UserProfile />} />
            <Route path="/profile" element={
              <ProtectedRoute>
                <MyJourney />
              </ProtectedRoute>
            } />
            <Route path="/instructor-slots/:instructorId" element={
              <ProtectedRoute>
                <InstructorSlots />
              </ProtectedRoute>
            } />
            <Route path="/reset-password" element={
              <ResetPasswordRoute>
                <ResetPassword />
              </ResetPasswordRoute>
            } />
            <Route path="/reset-password-sent" element={<ResetPasswordSent />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/terms-of-use" element={
              <PublicAccessRoute>
                <TermsOfUse />
              </PublicAccessRoute>
            } />
            <Route path="/privacy-policies" element={
              <PublicAccessRoute>
                <PrivacyPolicies />
              </PublicAccessRoute>
            } />
            <Route path="/invite-friend" element={
              <ProtectedRoute>
                <InviteFriend />
              </ProtectedRoute>
            } />
            <Route path="/contact" element={
              <ProtectedRoute>
                <Contact />
              </ProtectedRoute>
            } />
            <Route path="/search" element={
              <ProtectedRoute>
                <Search />
              </ProtectedRoute>
            } />
            <Route path="/post/:postId" element={
              <ProtectedRoute>
                <PostDetail />
              </ProtectedRoute>
            } />
            <Route path="/calendar" element={
              <ProtectedRoute>
                <Calendar />
              </ProtectedRoute>
            } />
            {/* Profile Detail Pages */}
            <Route path="/user/posts/:userId" element={
              <ProtectedRoute>
                <UserPostsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/connections/:userId" element={
              <ProtectedRoute>
                <UserConnectionsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/achievements/:userId" element={
              <ProtectedRoute>
                <UserAchievementsRoute />
              </ProtectedRoute>
            } />
            <Route path="/user/recognitions/:userId" element={
              <ProtectedRoute>
                <UserRecognitionsRoute />
              </ProtectedRoute>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
  );
};

const App = () => {
  const basename = getBasename();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Router basename={basename}>
          <I18nProvider>
            <AuthProvider>
              <FeedFilterProvider>
                <Toaster />
                <Sonner />
                <AppRoutes />
              </FeedFilterProvider>
            </AuthProvider>
          </I18nProvider>
        </Router>
      </TooltipProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};

export default App;
